'use client';

import { c as _c } from "react/compiler-runtime";
import { useAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import sessionAtom from '@/state/session';
import { usePrevious } from './hooks';
import type { Session } from 'next-auth';
export type SessionOrNull = Session | null;

/*
   Waits for useSessionState to set the session.
   useSessionState updates it to always have the latest token available.
*/
function initClientSession() {
  let sessionSet = false;
  let session: SessionOrNull = null;
  let resolvePromise: ((value: true) => void) | null = null;
  const sessionPromise = new Promise(r => {
    resolvePromise = r;
  });
  return {
    async getSession() {
      if (session || sessionSet) return session;
      await sessionPromise;
      return session;
    },
    setSession(newSession: SessionOrNull) {
      session = newSession;
      sessionSet = true;
      if (resolvePromise) resolvePromise(true);
    }
  };
}
const {
  getSession: getClientSession,
  setSession
} = initClientSession();
export { getClientSession };
export default function useSessionState() {
  const $ = _c(15);
  const currentSession = useSession();
  const pathname = usePathname();
  const [session, setSessionState] = useAtom(sessionAtom);
  const previousSession = usePrevious(currentSession);
  setSession(currentSession?.data);
  if (currentSession?.status === "unauthenticated" && previousSession?.status === "authenticated") {
    window.location.reload();
  }
  let t0;
  if ($[0] !== currentSession || $[1] !== session || $[2] !== setSessionState) {
    t0 = () => {
      if (currentSession?.status === "unauthenticated") {
        setSessionState(null);
        return;
      }
      if (currentSession?.status !== "authenticated" || currentSession.data.accessToken === session?.accessToken) {
        return;
      }
      if (!session) {
        setSessionState(currentSession.data);
      } else {
        Object.assign(session as Session, currentSession.data);
      }
    };
    $[0] = currentSession;
    $[1] = session;
    $[2] = setSessionState;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = session?.accessToken;
  let t2;
  if ($[4] !== currentSession || $[5] !== session || $[6] !== setSessionState || $[7] !== t1) {
    t2 = [t1, currentSession, session, setSessionState];
    $[4] = currentSession;
    $[5] = session;
    $[6] = setSessionState;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t0, t2);
  let t3;
  if ($[9] !== currentSession?.data?.error || $[10] !== pathname) {
    t3 = () => {
      if (currentSession?.data?.error !== "RefreshAccessTokenError") {
        return;
      }
      if (pathname === "/app/log-in") {
        return;
      }
      const target = `${window.location.pathname}${window.location.search}`;
      window.location.href = `/app/log-in?callbackUrl=${encodeURIComponent(target)}`;
    };
    $[9] = currentSession?.data?.error;
    $[10] = pathname;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== currentSession || $[13] !== pathname) {
    t4 = [currentSession, pathname];
    $[12] = currentSession;
    $[13] = pathname;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  useEffect(t3, t4);
}